// React
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { setPatient } from "Store/PatientSlice";
import { selectUserEmail } from "Store/AuthSlice";
import { openSnackbar } from "Store/SnackbarSlice";
import { setClient } from "Store/ClientSlice";

// i18n
import { useTranslation } from "react-i18next";

// Components
import HowItWorksCard from "Components/HowItWorksCard";
import PatientCards from "../PatientCards";

// Resources
import { getPatientsByUserEmail } from "API/Resource/patient";
import { getClientByDomain } from "API/Resource/client";

// Utils
import { getClientDomain } from "Utils/getClientDomain";

// MUI
import {
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Stack,
} from "@mui/material";

// Styles
import { styles } from "./styles";

// Icons
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";

const PatientHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const email = useSelector(selectUserEmail);
  const patientRef = useRef(null);

  useEffect(() => {
    const domain = getClientDomain();
    const fetchClient = async () => {
      const clientData = await getClientByDomain(domain);
      dispatch(setClient(clientData));
    };
    fetchClient();
  }, [dispatch]);

  useEffect(() => {
    handleFetchPatients();
  }, []);

  const handleFetchPatients = async () => {
    try {
      setIsLoading(true);
      const { patients } = await getPatientsByUserEmail(email);

      setPatients(
        patients.sort(
          (a, b) =>
            a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase()) ||
            a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase())
        )
      );
    } catch (error) {
      dispatch(openSnackbar({ message: error.message, severity: "error" }));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPatients = patients.filter((patient) => {
    const name = `${patient.firstName} ${patient.lastName}`;
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Stack alignItems="center" sx={{ width: "100%", minWidth: 300 }}>
      <HowItWorksCard />
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={styles.searchGrid}
      >
        <TextField
          placeholder={t("patientHome.searchPlaceholder")}
          variant="outlined"
          size="small"
          sx={{ width: "300px" }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          sx={{ width: { xs: "300px", md: "auto" } }}
          onClick={() => {
            dispatch(setPatient({}));
            navigate("/PatientForm", { state: { patientRef } });
          }}
        >
          {t("patientHome.addNewPatient")}
        </Button>
      </Stack>
      {isLoading ? (
        <CircularProgress sx={styles.loading} />
      ) : (
        <PatientCards
          patients={filteredPatients}
          fetchPatients={handleFetchPatients}
        />
      )}
    </Stack>
  );
};

export default PatientHome;
